import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Search, RefreshCw, AlertTriangle, CheckCircle, Clock, Package, User, FileText, Truck, Download } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { exportArrayToCsv } from '@/lib/csvExport';

const FGInvestigationQueue = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('slip') || '');

  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [resolution, setResolution] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [resolving, setResolving] = useState(false);

  const fetchItems = async (overrideQuery) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      params.append('limit', '1000');
      const q = (overrideQuery !== undefined ? overrideQuery : searchQuery).trim();
      if (q) params.append('pick_slip_number', q);
      const res = await apiClient.get(`/wms/fg/investigation-queue?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to fetch FG investigation queue');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => { fetchItems(searchQuery); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchQuery]);

  const exportCsv = () => {
    if (!items.length) {
      toast.error('Nothing to export — the queue is empty for this filter.');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
    exportArrayToCsv(
      items,
      [
        { key: 'investigation_id', header: 'Investigation ID' },
        { key: (r) => (
          r.type === 'fg_put_away_skipped' ? 'Put-Away'
          : r.type === 'fg_pick_item_partial_skipped' ? 'Partial-Skip'
          : 'Pick-Skip'
        ), header: 'Type' },
        { key: 'pick_slip_number', header: 'Slip / Put-Away ID' },
        { key: 'item_index', header: 'Item Index' },
        { key: 'sku_code', header: 'SKU' },
        { key: 'design_name', header: 'Design' },
        { key: 'shade', header: 'Shade' },
        { key: 'size', header: 'Size' },
        { key: 'ordered_qty', header: 'Ordered Qty' },
        { key: 'picked_qty', header: 'Picked / Placed Qty' },
        { key: 'remaining_qty', header: 'Remaining Qty' },
        { key: 'reason', header: 'Reason' },
        { key: 'reason_details', header: 'Reason Details' },
        { key: 'priority', header: 'Priority' },
        { key: 'customer', header: 'Customer' },
        { key: 'transport_name', header: 'Transport' },
        { key: 'reported_by', header: 'Reported By' },
        { key: 'reported_by_name', header: 'Reported By Name' },
        { key: 'reported_at', header: 'Reported At' },
        { key: 'status', header: 'Status' },
        { key: 'resolution', header: 'Resolution' },
        { key: 'action_taken', header: 'Action Taken' },
        { key: 'resolved_by', header: 'Resolved By' },
        { key: 'resolved_at', header: 'Resolved At' },
        { key: (r) => (r.picked_usns || []).join('|'), header: 'Picked USNs' },
        { key: (r) => (r.skipped_usns || []).join('|'), header: 'Skipped USNs' },
      ],
      `fg_investigation_queue_${statusFilter}_${stamp}.csv`,
    );
    toast.success(`Exported ${items.length} ticket${items.length === 1 ? '' : 's'} to CSV.`);
  };

  const openResolve = (it) => {
    setSelectedItem(it);
    setResolution('');
    setActionTaken('');
    setShowResolveDialog(true);
  };

  const handleResolve = async () => {
    if (!resolution) { toast.error('Pick a resolution'); return; }
    setResolving(true);
    try {
      await apiClient.post(
        `/wms/fg/investigation-queue/${selectedItem.investigation_id}/resolve`,
        null,
        { params: { resolution, action_taken: actionTaken } }
      );
      toast.success(
        resolution === 'found'
          ? 'Item recovered — pick line restored on the slip.'
          : 'Investigation resolved.'
      );
      setShowResolveDialog(false);
      setSelectedItem(null);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const priorityBadge = (p) => {
    const cls = p === 'high'
      ? 'bg-red-100 text-red-700 border-red-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
    return <Badge variant="outline" className={`${cls} text-[10px] uppercase`}>{p || 'medium'}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="fg-investigation-queue-page">
      <header className="bg-[#1A1F2E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-white/10" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300" /> FG Investigation Queue
          </h1>
          <p className="text-xs text-gray-300">Items skipped during FG picking — supervisor review & action</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-gray-500 block mb-1">Pick slip number</label>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search slip / SKU / customer…"
                data-testid="fg-inv-search"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="fg-inv-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => fetchItems()} data-testid="fg-inv-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={!items.length}
              data-testid="fg-inv-export-csv"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Download the currently visible queue as CSV (respects filters)."
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
            <Badge variant="outline" className="h-10 px-3 flex items-center" data-testid="fg-inv-count">
              {items.length} ticket{items.length === 1 ? '' : 's'}
            </Badge>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
            No {statusFilter === 'pending' ? 'pending' : ''} investigations.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <Card key={it.investigation_id} data-testid={`fg-inv-${it.investigation_id}`}>
                <CardContent className="p-4 grid grid-cols-12 gap-3">
                  <div className="col-span-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3 h-3 text-gray-400" />
                      <span className="font-mono text-sm font-bold">
                        {it.type === 'fg_put_away_skipped'
                          ? 'PA'
                          : it.type === 'fg_pick_item_partial_skipped'
                            ? 'PS-P'
                            : 'PS'} #{it.pick_slip_number || '-'}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          it.type === 'fg_put_away_skipped'
                            ? 'bg-amber-50 text-amber-700 border-amber-200 text-[9px] uppercase'
                            : it.type === 'fg_pick_item_partial_skipped'
                              ? 'bg-purple-50 text-purple-700 border-purple-200 text-[9px] uppercase'
                              : 'bg-blue-50 text-blue-700 border-blue-200 text-[9px] uppercase'
                        }
                        data-testid={`fg-inv-type-${it.investigation_id}`}
                      >
                        {it.type === 'fg_put_away_skipped'
                          ? 'Put-Away'
                          : it.type === 'fg_pick_item_partial_skipped'
                            ? 'Partial-Skip'
                            : 'Pick'}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {(it.reported_at || '').slice(0, 16).replace('T', ' ')}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {it.reported_by_name || it.reported_by || '—'}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      {priorityBadge(it.priority)}
                      {it.status === 'resolved' && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] uppercase">
                          {it.resolution || 'Resolved'}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="col-span-4 space-y-1">
                    <div className="flex items-center gap-2">
                      <Package className="w-3 h-3 text-gray-400" />
                      <span className="font-mono text-xs font-semibold">{it.sku_code || '—'}</span>
                    </div>
                    <p className="text-sm font-medium">{it.design_name || it.item_name || '—'}</p>
                    <p className="text-xs text-gray-500">
                      {[it.shade, it.size].filter(Boolean).join(' / ') || '—'}
                    </p>
                  </div>

                  <div className="col-span-2 space-y-1">
                    <p className="text-xs text-gray-500">Qty</p>
                    <p className="text-sm font-mono">
                      <span className="font-bold">{it.remaining_qty ?? 0}</span>
                      <span className="text-gray-400"> / {it.ordered_qty ?? 0}</span>
                    </p>
                    <p className="text-[10px] text-gray-400">picked: {it.picked_qty ?? 0}</p>
                  </div>

                  <div className="col-span-3 space-y-1 text-right">
                    {it.customer && (
                      <p className="text-xs text-gray-700 truncate" title={it.customer}>
                        {it.customer}
                      </p>
                    )}
                    {it.transport_name && (
                      <p className="text-[11px] text-gray-500 flex items-center gap-1 justify-end">
                        <Truck className="w-3 h-3" /> {it.transport_name}
                      </p>
                    )}
                    {it.reason && (
                      <p className="text-xs italic text-amber-700 bg-amber-50 rounded px-2 py-1 inline-block">
                        {it.reason}
                      </p>
                    )}
                  </div>

                  <div className="col-span-12 flex items-center justify-end gap-2 pt-2 border-t">
                    {it.status !== 'resolved' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                          onClick={() => openResolve(it)}
                          data-testid={`resolve-btn-${it.investigation_id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Resolve
                        </Button>
                      </>
                    )}
                    {it.status === 'resolved' && it.action_taken && (
                      <p className="text-xs text-gray-500 italic">{it.action_taken}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent data-testid="fg-inv-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" /> Resolve FG Investigation
            </DialogTitle>
            <DialogDescription>
              Slip <span className="font-mono">PS #{selectedItem?.pick_slip_number}</span>
              &nbsp;— {selectedItem?.design_name || selectedItem?.item_name}
              &nbsp;({selectedItem?.shade || '—'} / {selectedItem?.size || '—'})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Resolution</label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger data-testid="fg-inv-resolution-select"><SelectValue placeholder="Pick one…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="found">Found — restore the pick line</SelectItem>
                  <SelectItem value="relocated">Relocated — picked from another bin</SelectItem>
                  <SelectItem value="written_off">Written off — confirmed missing/lost</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Action / note (optional)</label>
              <Textarea
                rows={3}
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                placeholder="What did you do? e.g. Found in F03-B-14-D, picked manually."
                data-testid="fg-inv-action-textarea"
              />
            </div>
            {resolution === 'found' && (
              <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
                The skipped item will be restored to <b>PENDING</b> on the original slip
                so the picker can complete it.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)} data-testid="fg-inv-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!resolution || resolving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="fg-inv-confirm-btn"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              {resolving ? 'Resolving…' : 'Resolve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGInvestigationQueue;
